import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Plus,
  Pencil,
  Trash2,
  Download,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Receipt,
  FileText,
} from 'lucide-react';
import { INCOME_STATUS_COLORS } from './crmConstants';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAYMENT_METHODS = ['Abanca', 'Tarjeta Abanca', 'Wise', 'Paypal'];

// Fetch file as blob (with auth) and open in new tab — avoids SPA router intercept
const openInvoiceFile = async (url, originalName) => {
  try {
    const resp = await axios.get(url, { responseType: 'blob' });
    const blobUrl = window.URL.createObjectURL(new Blob([resp.data], { type: resp.headers['content-type'] || 'application/octet-stream' }));
    const win = window.open(blobUrl, '_blank', 'noopener,noreferrer');
    if (!win) {
      // Popup blocked — fallback: trigger a download
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = originalName || 'factura';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    // Revoke after some time so the tab can finish loading
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
  } catch (err) {
    toast.error(err.response?.status === 404 ? 'Archivo no encontrado' : 'Error al abrir factura');
  }
};

export const ExpensesView = ({
  expenses,
  expenseStats,
  expensePagination,
  expenseSearch,
  setExpenseSearch,
  expensePaymentFilter,
  setExpensePaymentFilter,
  fetchExpenses,
  openExpenseDialog,
  handleDeleteExpense,
  setShowExpenseDownloadDialog,
  // Income invoices props
  incomeInvoices,
  openIncomeDialog,
  handleDeleteIncome,
  setShowIncomeDownloadDialog,
  language,
  t,
}) => {
  return (
    <>
      {/* Expense Stats */}
      {expenseStats && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          <Card className="border-l-4 border-l-rose-500 shadow-md hover:shadow-lg transition-shadow">
            <CardHeader className="pb-2">
              <CardDescription className="text-slate-500">{t.totalExpenses}</CardDescription>
              <CardTitle className="text-2xl font-bold text-rose-600">{expenseStats.total_expenses}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="border-l-4 border-l-amber-500 bg-amber-50/50 shadow-md hover:shadow-lg transition-shadow">
            <CardHeader className="pb-2">
              <CardDescription className="text-slate-500">{t.totalBase}</CardDescription>
              <CardTitle className="text-2xl font-bold text-amber-600">€{expenseStats.total_base?.toLocaleString()}</CardTitle>
            </CardHeader>
          </Card>
          <Card className="border-l-4 border-l-red-500 bg-red-50/50 shadow-md hover:shadow-lg transition-shadow">
            <CardHeader className="pb-2">
              <CardDescription className="text-slate-500">{t.total}</CardDescription>
              <CardTitle className="text-2xl font-bold text-red-600">€{expenseStats.total_amount?.toLocaleString()}</CardTitle>
            </CardHeader>
          </Card>
        </div>
      )}

      {/* Expense Filters and Add Button */}
      <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
        <div className="flex flex-wrap gap-2">
          <Input
            placeholder={t.search}
            value={expenseSearch}
            onChange={(e) => setExpenseSearch(e.target.value)}
            className="w-48"
          />
          <Select value={expensePaymentFilter} onValueChange={setExpensePaymentFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder={t.paymentMethod} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{language === 'es' ? 'Todas' : 'All'}</SelectItem>
              {PAYMENT_METHODS.map(pm => (
                <SelectItem key={pm} value={pm}>{pm}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={() => fetchExpenses()} className="hover:bg-slate-100">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowExpenseDownloadDialog(true)}>
            <Download className="h-4 w-4 mr-2" />
            {language === 'es' ? 'Descargar' : 'Download'}
          </Button>
          <Button onClick={() => openExpenseDialog()} data-testid="add-expense-btn" className="bg-rose-600 hover:bg-rose-700 text-white shadow-md">
            <Plus className="h-4 w-4 mr-2" />
            {t.newExpense}
          </Button>
        </div>
      </div>

      {/* Expenses Table */}
      <Card className="shadow-md border-slate-200 mt-4">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>{t.invoiceNumber}</TableHead>
                <TableHead>{t.orderNumber}</TableHead>
                <TableHead>{t.provider}</TableHead>
                <TableHead>{t.baseAmount}</TableHead>
                <TableHead>{t.iva}</TableHead>
                <TableHead>{t.irpf}</TableHead>
                <TableHead>{t.total}</TableHead>
                <TableHead>{t.expenseStatus}</TableHead>
                <TableHead>{t.paymentMethod}</TableHead>
                <TableHead>{t.expenseInvoiceDate}</TableHead>
                <TableHead>{t.createdDate}</TableHead>
                <TableHead className="text-right">{t.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {expenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} className="text-center py-8 text-muted-foreground">
                    {t.noExpenses}
                  </TableCell>
                </TableRow>
              ) : (
                expenses.map((expense) => {
                  const files = expense.attached_files || expense.files || [];
                  const firstFile = files[0];
                  return (
                  <TableRow key={expense.id}>
                    <TableCell className="font-medium">
                      {firstFile ? (
                        <button
                          type="button"
                          onClick={() => openInvoiceFile(`${API_URL}/api/admin/expenses/${expense.id}/files/${firstFile.stored_name}`, firstFile.original_name)}
                          className="text-blue-600 hover:underline inline-flex items-center gap-1"
                          title={firstFile.original_name}
                          data-testid={`expense-invoice-link-${expense.invoice_number}`}
                        >
                          {expense.invoice_number}
                          <FileText className="h-3 w-3" />
                        </button>
                      ) : (
                        expense.invoice_number
                      )}
                    </TableCell>
                    <TableCell>
                      {expense.order_lines && expense.order_lines.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {expense.order_lines.slice(0, 3).map((l, i) => (
                            <Badge key={i} variant="outline">#{l.order_number}</Badge>
                          ))}
                          {expense.order_lines.length > 3 && (
                            <Badge variant="outline">+{expense.order_lines.length - 3}</Badge>
                          )}
                        </div>
                      ) : expense.order_number ? (
                        <Badge variant="outline">#{expense.order_number}</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>{expense.provider}</TableCell>
                    <TableCell>€{expense.base_amount?.toFixed(2)}</TableCell>
                    <TableCell>€{expense.iva?.toFixed(2) || '0.00'}</TableCell>
                    <TableCell>€{expense.irpf?.toFixed(2) || '0.00'}</TableCell>
                    <TableCell className="font-semibold text-red-600">€{expense.total?.toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge className={
                        expense.status === 'Pagada' ? 'bg-green-500 hover:bg-green-600' :
                        expense.status === 'Pagada 50%' ? 'bg-yellow-500 hover:bg-yellow-600' :
                        expense.status === 'Con proforma' ? 'bg-blue-500 hover:bg-blue-600' :
                        'bg-red-500 hover:bg-red-600'
                      }>
                        {expense.status || 'No pagada'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{expense.payment_method}</Badge>
                    </TableCell>
                    <TableCell>
                      {expense.invoice_date ? new Date(expense.invoice_date).toLocaleDateString('es-ES', {day: '2-digit', month: '2-digit', year: 'numeric'}) : '-'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {expense.created_date ? new Date(expense.created_date).toLocaleDateString('es-ES', {day: '2-digit', month: '2-digit', year: 'numeric'}) : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openExpenseDialog(expense)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteExpense(expense.id)} className="text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Expense Pagination */}
      {expensePagination.total_pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            disabled={expensePagination.page <= 1}
            onClick={() => fetchExpenses(expensePagination.page - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm">
            {t.page} {expensePagination.page} {t.of} {expensePagination.total_pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={expensePagination.page >= expensePagination.total_pages}
            onClick={() => fetchExpenses(expensePagination.page + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* ===== INCOME INVOICES SECTION ===== */}
      <div className="mt-12 pt-8 border-t-2 border-green-200">
        <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center mb-4">
          <h3 className="text-xl font-bold text-green-700 flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            {language === 'es' ? 'Facturas de Ingresos' : 'Income Invoices'}
          </h3>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowIncomeDownloadDialog(true)}>
              <Download className="h-4 w-4 mr-2" />
              {language === 'es' ? 'Descargar' : 'Download'}
            </Button>
            <Button onClick={() => openIncomeDialog()} className="bg-green-600 hover:bg-green-700 text-white shadow-md">
              <Plus className="h-4 w-4 mr-2" />
              {language === 'es' ? 'Añadir Ingreso' : 'Add Income'}
            </Button>
          </div>
        </div>

        {/* Income Table */}
        <Card className="shadow-md border-green-200">
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-green-50">
                  <TableHead>{t.invoiceNumber}</TableHead>
                  <TableHead>{language === 'es' ? 'Cliente' : 'Client'}</TableHead>
                  <TableHead>{language === 'es' ? 'NIF/CIF' : 'Tax ID'}</TableHead>
                  <TableHead>{t.baseAmount}</TableHead>
                  <TableHead>{t.total}</TableHead>
                  <TableHead>{language === 'es' ? 'Estado' : 'Status'}</TableHead>
                  <TableHead>{language === 'es' ? 'Fecha' : 'Date'}</TableHead>
                  <TableHead className="text-right">{t.actions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {incomeInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      {language === 'es' ? 'No hay facturas de ingresos' : 'No income invoices'}
                    </TableCell>
                  </TableRow>
                ) : (
                  incomeInvoices.map((inv) => {
                    const incFiles = inv.attached_files || inv.files || [];
                    const incFirstFile = incFiles[0];
                    return (
                    <TableRow key={inv.id} className="hover:bg-green-50/50">
                      <TableCell className="font-medium">
                        {incFirstFile ? (
                          <button
                            type="button"
                            onClick={() => openInvoiceFile(`${API_URL}/api/admin/income-invoices/${inv.id}/files/${incFirstFile.stored_name}`, incFirstFile.original_name)}
                            className="text-blue-600 hover:underline inline-flex items-center gap-1"
                            title={incFirstFile.original_name}
                          >
                            {inv.invoice_number || '-'}
                            <FileText className="h-3 w-3" />
                          </button>
                        ) : (
                          inv.invoice_number || '-'
                        )}
                      </TableCell>
                      <TableCell>{inv.client_name || '-'}</TableCell>
                      <TableCell>{inv.client_nif || '-'}</TableCell>
                      <TableCell>€{inv.base_amount?.toLocaleString() || 0}</TableCell>
                      <TableCell className="font-bold text-green-600">€{inv.total?.toLocaleString() || 0}</TableCell>
                      <TableCell>
                        <Badge className={INCOME_STATUS_COLORS[inv.status] || 'bg-gray-100'}>
                          {inv.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{inv.invoice_date ? inv.invoice_date.split('T')[0] : '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openIncomeDialog(inv)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteIncome(inv.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default ExpensesView;
